import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaExternalLinkAlt, FaGithub, FaFilter } from 'react-icons/fa';
import { staticPortfolios } from '../config/staticData';
import { pageSeoData } from '../config/pageSeoConfig';
import './Portfolio.scss';


interface PortfolioProject {
  id: number;
  title: string;
  slug: string;
  category: string;
  image: string;
  description: string;
  technologies: string[];
  liveUrl?: string;
  githubUrl?: string;
  featured: boolean;
}

const Portfolio: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'demo' | 'delivered'>('demo');
  const [activeFilter, setActiveFilter] = useState('all');
  const [portfolios, setPortfolios] = useState<PortfolioProject[]>([]);

  useEffect(() => {
    document.title = 'Portfolio - Our Best Projects & Work | App Developer Pro';
  }, []);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Array<{ key: string; label: string; count: number }>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // Show 9 portfolios per page

  // Demo projects placeholder data (3 per row)
  const demoProjects = [
     {
      id: 107,
      title: 'Automated Hotel Management System',
      slug: 'automated-hotel-management-system',
      category: 'saas',
      image: '/images/hotelopspro.png',
      description: 'DigiLearn is a modern, interactive, and scalable e-learning platform designed to deliver high-quality courses, real-time assessments, community interaction, and personalized learning experiences.',
      technologies: ['Vue.js', 'Laravel', 'MySQL'],
      liveUrl: 'https://appdeveloperpro.com/hotelopspro/',
      githubUrl: '#',
      featured: false
    },
    {
      id: 101,
      title: 'School Management System',
      slug: 'school-management-system',
      category: 'web',
      image: '/images/digital-school-app-developer-pro.png',
      description: 'Automated School Management System serving as a centralized platform connecting administrators, teachers, parents, and students.',
      technologies: ['React', 'TypeScript', 'Node.js', 'MongoDB'],
      liveUrl: 'https://appdeveloperpro.com/digitalschool/',
      githubUrl: '#',
      featured: true
    },
    {
      id: 102,
      title: 'Hospital Management System',
      slug: 'hospital-management-system',
      category: 'mobile',
      image: '/images/hospisoft-app-developer-pro.png',
      description: 'Modern SaaS Hospital Management System (HMS) designed to automate and optimize all clinical, administrative, and financial workflows with real-time data accessibility.',
      technologies: ['React Native', 'Firebase', 'Redux'],
      liveUrl: 'https://appdeveloperpro.com/hospisoft/',
      githubUrl: '#',
      featured: true
    },
    {
      id: 103,
      title: 'E-Commerce Platform',
      slug: 'e-commerce-platform',
      category: 'design',
      image: '/images/shopifyo-app-developer-pro.png',
      description: 'Modern SaaS-Based eCommerce Platform designed to empower retailers, wholesalers, and D2C brands with an all-in-one solution that streamlines product management, orders, payments, and customer engagement.',
      technologies: ['nextjs', 'typescript', 'mongodb'],
      liveUrl: 'https://appdeveloperpro.com/shopifyo/',
      githubUrl: '#',
      featured: true
    },
    {
      id: 104,
      title: 'Food Platform for Restaurants',
      slug: 'food-platform-for-restaurants',
      category: 'web',
      image: '/images/food-platform-for-restaurants-app-developer-pro.png',
      description: 'SaaS-Based Food Web Development Platform—a fully cloud-hosted, scalable, and customizable solution that enables businesses to manage online orders, menus, customers, payments, loyalty programs, and delivery operations from one powerful dashboard',
      technologies: ['Next.js', 'PostgreSQL', 'AWS'],
      liveUrl: 'https://appdeveloperpro.com/foodorax/',
      githubUrl: '#',
      featured: false
    },
    {
      id: 105,
      title: 'Advocate Cloud',
      slug: 'advocate-cloud',
      category: 'mobile',
      image: '/images/advocate-cloud-app-developer-pro.png',
      description: 'Custom, secure, and high-performance web application tailored specifically for a Supreme Court Advocate in India. The platform will enhance client management, case workflow, document handling, and legal research—all within a secure cloud-based environment.',
      technologies: ['react native', 'expo'],
      liveUrl: 'https://appdeveloperpro.com/advocatecloud/',
      githubUrl: '#',
      featured: false
    },
    {
      id: 106,
      title: 'E-Learning Platform',
      slug: 'e-learning-platform',
      category: 'saas',
      image: '/images/e-learning-platform-app-developer-pro.png',
      description: 'DigiLearn is a modern, interactive, and scalable e-learning platform designed to deliver high-quality courses, real-time assessments, community interaction, and personalized learning experiences.',
      technologies: ['Vue.js', 'Laravel', 'MySQL'],
      liveUrl: 'https://appdeveloperpro.com/digilearn/',
      githubUrl: '#',
      featured: false
    },
     {
      id: 107,
      title: 'Pharmacy Management System',
      slug: 'pharmacy-management-system',
      category: 'saas',
      image: '/images/pharmasoft.png',
      description: 'DigiLearn is a modern, interactive, and scalable e-learning platform designed to deliver high-quality courses, real-time assessments, community interaction, and personalized learning experiences.',
      technologies: ['Vue.js', 'Laravel', 'MySQL'],
      liveUrl: 'https://appdeveloperpro.com/pharmasoft/login',
      githubUrl: '#',
      featured: false
    }
  ];

  // SEO constants from config
  const seoTitle = pageSeoData.portfolio.title;
  const seoDescription = pageSeoData.portfolio.description;
  const seoKeywords = pageSeoData.portfolio.keywords;
  const seoUrl = pageSeoData.portfolio.url;
  const seoImage = "https://appdeveloperpro.com/images/og-portfolio.jpg";
  
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": "https://appdeveloperpro.com/portfolio#webpage",
        "url": seoUrl,
        "name": seoTitle,
        "description": seoDescription,
        "inLanguage": "en-US",
        "isPartOf": {
          "@id": "https://appdeveloperpro.com/#website"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": seoImage
        }
      }
    ]
  };

  useEffect(() => {
    // Use static data instead of API
    setPortfolios(staticPortfolios);
    
    // Calculate categories from static data
    const categoryCounts: { [key: string]: number } = {};
    staticPortfolios.forEach(p => {
      categoryCounts[p.category] = (categoryCounts[p.category] || 0) + 1;
    });
    
    const categoryList = [
      { key: 'all', label: 'All Projects', count: staticPortfolios.length },
      { key: 'web', label: 'Web Development', count: categoryCounts['web'] || 0 },
      { key: 'mobile', label: 'Mobile Apps', count: categoryCounts['mobile'] || 0 },
      { key: 'design', label: 'UI/UX Design', count: categoryCounts['design'] || 0 },
      { key: 'ecommerce', label: 'E-commerce', count: categoryCounts['ecommerce'] || 0 },
      { key: 'saas', label: 'SaaS Products', count: categoryCounts['saas'] || 0 }
    ].filter(cat => cat.key === 'all' || cat.count > 0);
    
    setCategories(categoryList);
    setLoading(false);
  }, []);

  const filteredProjects = activeFilter === 'all' 
    ? portfolios 
    : portfolios.filter(portfolio => portfolio.category === activeFilter);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="App Developer Pro" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#3b82f6" />
        <meta name="msapplication-TileColor" content="#3b82f6" />
        <meta name="rating" content="general" />
        <meta name="distribution" content="global" />
        <meta name="language" content="en" />
        <meta name="revisit-after" content="7 days" />
        <meta name="expires" content="never" />
        <meta name="coverage" content="worldwide" />
        <meta name="target" content="all" />
        <meta name="HandheldFriendly" content="true" />
        <meta name="MobileOptimized" content="320" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:site_name" content="App Developer Pro" />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Portfolio of Web & App Projects - App Developer Pro" />
        <meta property="og:locale" content="en_US" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@appdeveloperpro" />
        <meta name="twitter:creator" content="@appdeveloperpro" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:image:alt" content="Portfolio of Web & App Projects - App Developer Pro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="portfolio">
        <section className="portfolio__hero">
          <div className="portfolio__hero-content">
            <h1>Our Portfolio</h1>
            <p>
              Explore our latest projects and see how we've helped businesses
              achieve their digital goals with innovative solutions.
            </p>
          </div>
        </section>

        {/* Tabs Section - Demo Projects / Delivered Projects */}
        <section className="portfolio__tabs">
          <div className="portfolio__tabs-buttons">
            <button
              className={`portfolio__tab-btn ${activeTab === 'demo' ? 'portfolio__tab-btn--active' : ''}`}
              onClick={() => {
                setActiveTab('demo');
                setCurrentPage(1);
              }}
            >
              Demo Projects
            </button>
            <button
              className={`portfolio__tab-btn ${activeTab === 'delivered' ? 'portfolio__tab-btn--active' : ''}`}
              onClick={() => {
                setActiveTab('delivered');
                setCurrentPage(1);
              }}
            >
              Delivered Projects
            </button>
          </div>
        </section>

        {/* Show filters only for Delivered Projects tab */}
        {activeTab === 'delivered' && (
          <section className="portfolio__filters">
            <div className="portfolio__filter-buttons">
              {categories.length > 0 ? (
                categories.map((filter) => (
                  <button
                    key={filter.key}
                    className={`portfolio__filter-btn ${
                      activeFilter === filter.key ? 'portfolio__filter-btn--active' : ''
                    }`}
                    onClick={() => setActiveFilter(filter.key)}
                  >
                    <FaFilter /> {filter.label} {filter.count > 0 && `(${filter.count})`}
                  </button>
                ))
              ) : (
                <button
                  className="portfolio__filter-btn portfolio__filter-btn--active"
                >
                  <FaFilter /> All Projects
                </button>
              )}
            </div>
          </section>
        )}

        {/* Demo Projects Content */}
        {activeTab === 'demo' && (
          <section className="portfolio__grid">
            <h2 className="portfolio__section-title">Demo Projects</h2>
            <div className="portfolio__projects-wrapper">
              {demoProjects.map((project) => (
                <div
                  key={project.id}
                  className="portfolio__card"
                >
                  <div className="portfolio__card-image">
                    <img 
                      src={project.image}
                      alt={project.title}
                      loading="lazy"
                    />
                    <div className="portfolio__card-overlay">
                      <div className="portfolio__card-links">
                        {project.liveUrl && (
                          <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" title="View Live">
                            <FaExternalLinkAlt />
                          </a>
                        )}
                        {project.githubUrl && (
                          <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" title="View on GitHub">
                            <FaGithub />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="portfolio__card-content">
                    <div className="portfolio__card-header">
                      <span className="portfolio__card-category">{project.category}</span>
                      <h3>{project.title}</h3>
                    </div>
                    <p>{project.description}</p>
                    <div className="portfolio__card-technologies">
                      {project.technologies.map((tech, idx) => (
                        <span key={idx} className="portfolio__tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Delivered Projects Content */}
        {activeTab === 'delivered' && (
          <section className="portfolio__grid">
            <h2 className="portfolio__section-title">Delivered Projects</h2>
          
            {loading ? (
              <div className="portfolio__loading">
                <div className="portfolio__loader"></div>
                <p>Loading portfolio projects...</p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="portfolio__empty">
                <p>No projects found in this category.</p>
              </div>
            ) : (
              <>
                <div className="portfolio__projects-wrapper">
                  {currentProjects.map((project) => (
                    <div
                      key={project.id}
                      className="portfolio__card"
                    >
                      <div className="portfolio__card-image">
                        <img 
                          src={project.image}
                          alt={project.title}
                          loading="lazy"
                        />
                        <div className="portfolio__card-overlay">
                          <div className="portfolio__card-links">
                            {project.liveUrl && (
                              <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" title="View Live">
                                <FaExternalLinkAlt />
                              </a>
                            )}
                            {project.githubUrl && (
                              <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" title="View on GitHub">
                                <FaGithub />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="portfolio__card-content">
                        <div className="portfolio__card-header">
                          <span className="portfolio__card-category">{project.category}</span>
                          <h3>{project.title}</h3>
                        </div>
                        <p>{project.description}</p>
                        <div className="portfolio__card-technologies">
                          {project.technologies.map((tech, idx) => (
                            <span key={idx} className="portfolio__tech-tag">{tech}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="portfolio__pagination">
                    <button
                      className="portfolio__pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  
                    <div className="portfolio__pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                        <button
                          key={pageNum}
                          className={`portfolio__pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>

                    <button
                      className="portfolio__pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}

                <div className="portfolio__pagination-info">
                  <p>Showing {startIndex + 1}-{Math.min(endIndex, filteredProjects.length)} of {filteredProjects.length} projects</p>
                </div>
              </>
            )}
          </section>
        )}

        {/* SEO Content Section - Portfolio Page */}
        <section className="portfolio__seo-intro">
          <h2>Our Work: Web, Mobile & Design Projects</h2>
          <p>App Developer Pro delivers high-impact <strong>web development</strong>, <strong>mobile app development</strong>, and <strong>UI/UX design</strong> projects for clients across industries. Our portfolio showcases our commitment to quality, innovation, and measurable business results.</p>
        </section>

        <section className="portfolio__unique-section">
          <h3>What Makes Our Portfolio Unique?</h3>
          <div className="portfolio__unique-list">
            <div className="portfolio__unique-item">
              <h4>Custom Solutions</h4>
              <p>Every project is tailored to the client's unique needs, business goals, and target audience. We don't believe in one-size-fits-all solutions.</p>
            </div>
            <div className="portfolio__unique-item">
              <h4>Modern Technologies</h4>
              <p>We use the latest frameworks and tools to build fast, secure, and scalable digital products that stand out in the market.</p>
            </div>
            <div className="portfolio__unique-item">
              <h4>Proven Results</h4>
              <p>Our work drives real outcomes—higher engagement, increased sales, and long-term growth for our clients.</p>
            </div>
          </div>
        </section>

        {/* New Industries Section - full width, below unique section */}
        <section className="portfolio__industries-main">
          <div className="portfolio__industries-main-container">
            <h3>Industries We Serve</h3>
            <div className="portfolio__industries-main-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gridGap: '20px' }}>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🏢</span> Startups & Entrepreneurs</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🛒</span> E-commerce & Retail</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">💊</span> Healthcare & Wellness</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">💳</span> Finance & Fintech</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🎓</span> Education & E-learning</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🏠</span> Real Estate & Property</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🚗</span> Automotive & Mobility</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🍔</span> Food & Restaurants</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">✈️</span> Travel & Hospitality</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">⚡</span> Energy & Utilities</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🏭</span> Manufacturing & Industrial</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🎮</span> Media & Entertainment</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🌱</span> Agriculture & AgriTech</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🛰️</span> Logistics & Supply Chain</div>
              <div className="portfolio__industry-card"><span className="portfolio__industry-icon">🧑‍💻</span> IT & Software Services</div>
            </div>
          </div>
        </section>

        <section className="portfolio__seo-cta">
          <h3>Ready to Start Your Project?</h3>
          <p>Contact App Developer Pro today for a free consultation and see how we can bring your digital vision to life.</p>
        </section>
      </div>
    </>
  );
};

export default Portfolio;

